import { Helmet } from 'react-helmet';
import { UsersMenu } from '../UsersMenu';
import React, { ChangeEvent, useEffect, useState } from 'react';
import {
  ConsolidationSupport,
  DuplicateDetection,
  StoreCapacityFlagsWithLabel,
  StoreRetailerFlagsWithLabel,
  StoreSettings,
  ValueBasedDiscount,
  WindowTypes,
} from './storeTypes';
import { fetchStoreInfo, saveStoreSettings } from '../../../fetch/fetchStore';
import { Loader } from '../../../components/Loader/Loader';
import { RadioComponent } from '../../../ReusableComponents/Radio/RadioComponent';
import { InputComponent } from '../../../ReusableComponents/Input/InputComponent';
import { CheckboxComponent } from '../../../ReusableComponents/Checkbox/CheckboxComponent';
import { ButtonComponent } from '../../../ReusableComponents/Button/ButtonComponent';
import { SwitchComponent } from '../../../ReusableComponents/Switch/SwitchComponent';
import { Link, useHistory } from 'react-router-dom';
import './EditStoreSettings.scss';
import assert from 'assert';
import { Snackbar } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { Region } from '../../../types/orderTypes';
import ThresholdInput from './ThresholdInput/ThresholdInput';

interface MatchParams {
  accountId: string;
}

export interface RouteComponentProps<P> {
  match: Match<P>;
}

export interface Match<P> {
  params: P;
}

interface ToastConfig {
  text: string;
  type: 'success' | 'error';
}

export interface ThresholdErrorField {
  threshold: boolean;
  discount: boolean;
}

interface ErrorFields {
  dailyCutOffTimeState: boolean;
  packagingCutoffState: boolean;
  largeOffsetCutOffState: boolean;
  shopHomePageState: boolean;
  displayBasePriceState: boolean;
  temperatureControlPriceAdjustment: boolean;
  discountThresholds: ThresholdErrorField[];
}

const errorFieldsObject: ErrorFields = {
  dailyCutOffTimeState: false,
  packagingCutoffState: false,
  largeOffsetCutOffState: false,
  shopHomePageState: false,
  displayBasePriceState: false,
  temperatureControlPriceAdjustment: false,
  discountThresholds: [],
};

const hasErrors = (errorFields: ErrorFields): boolean => {
  const { discountThresholds, ...rest } = errorFields;
  return discountThresholds
    .flatMap(Object.values)
    .concat(Object.values(rest))
    .some(value => value);
};

const StoreSettingsSection = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}): JSX.Element => {
  return (
    <div>
      <div className="store-settings-header-section">
        <h5>{title}</h5>
      </div>
      <div className="header-line" />
      <div className="store-edit-box">{children}</div>
    </div>
  );
};

const EditStoreSettings = (props: RouteComponentProps<MatchParams>) => {
  const [storeState, setStoreState] = useState<StoreSettings | undefined>(
    undefined
  );
  const [toastConfig, setToastConfig] = useState<ToastConfig | undefined>(
    undefined
  );
  const [inputErrorFields, setInputErrorFields] =
    useState<ErrorFields>(errorFieldsObject);
  const [isToastOpen, setToastOpen] = useState<boolean>(false);
  const history = useHistory();

  const accountId = props.match.params.accountId;
  useEffect(() => {
    const getStore = async (accountId: string) => {
      if (accountId) {
        setStoreState(await fetchStoreInfo(accountId));
      }
    };
    getStore(accountId);
  }, [accountId]);

  if (storeState === undefined) {
    return (
      <>
        <Helmet>
          <title>Users - Edit store settings</title>
        </Helmet>
        <UsersMenu />
        <div className="edit-store-settings-loader">
          <Loader type={'spinner'} size={'xxl'} />
        </div>
      </>
    );
  }

  const setOrClearStoreSettingsErrorFields = (
    field: string,
    value: string
  ): void => {
    if (
      (field === 'dailyCutOffTimeState' &&
        (!/^([01]?[0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]$/.test(value) ||
          value.trim() === '')) ||
      (field === 'displayBasePriceState' &&
        !/^\d+([.,]\d{1,2})?$/.test(value) &&
        value.trim() !== '') ||
      (field === 'temperatureControlPriceAdjustment' &&
        !/^-?\d+([.,]\d{1,2})?$/.test(value)) ||
      (field === 'shopHomePageState' && value.length < 3 && value.length > 0) ||
      (field === 'packagingCutoffState' &&
        (!/^([01]?[0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]$/.test(value) ||
          value.trim() === '')) ||
      (field === 'largeOffsetCutOffState' &&
        (!/^(0|-?[1-9][\d]*)$/.test(value) || value.trim() === '')) ||
      (field !== 'dailyCutOffTimeState' &&
        field !== 'shopHomePageState' &&
        field !== 'displayBasePriceState' &&
        field !== 'packagingCutoffState' &&
        field !== 'largeOffsetCutOffState' &&
        field !== 'temperatureControlPriceAdjustment' &&
        !/^\d+$/.test(value) &&
        value.trim() !== '')
    ) {
      setInputErrorFields(currentErrorFields => ({
        ...currentErrorFields,
        [field]: true,
      }));
    } else {
      setInputErrorFields(currentErrorFields => ({
        ...currentErrorFields,
        [field]: false,
      }));
    }
  };

  const saveSettings = async (): Promise<void> => {
    const result = await saveStoreSettings(storeState);
    if (result.type === 'success') {
      setToastConfig({
        text: `New configuration for ${storeState.name} was updated`,
        type: 'success',
      });
    } else {
      setToastConfig({ text: result.errorMessage, type: 'error' });
    }
    setToastOpen(true);
  };

  const onChangeDefaultOrderDuplication = (
    event: ChangeEvent<HTMLInputElement>
  ): void => {
    const value = event.target.value as DuplicateDetection;

    setStoreState((oldState): StoreSettings => {
      return { ...oldState, duplicateDetection: value } as StoreSettings;
    });
  };

  const onChangeDefaultConsolidationSupport = (
    event: ChangeEvent<HTMLInputElement>
  ): void => {
    const value = event.target.value as ConsolidationSupport;
    setStoreState((oldState): StoreSettings => {
      return { ...oldState, consolidationSupport: value } as StoreSettings;
    });
  };

  const onChangeRegions = (event: ChangeEvent<HTMLInputElement>): void => {
    const value = event.target.value as Region;

    if (storeState.deliveryRegions.includes(value)) {
      setStoreState((oldState): StoreSettings => {
        return {
          ...oldState,
          deliveryRegions: oldState?.deliveryRegions.filter(
            region => region !== value
          ),
        } as StoreSettings;
      });
    } else {
      setStoreState((oldState): StoreSettings => {
        assert(oldState);
        return {
          ...oldState,
          deliveryRegions: [...oldState.deliveryRegions, value],
        };
      });
    }
  };

  const onChangeWindows = (event: ChangeEvent<HTMLInputElement>): void => {
    const value = event.target.value;

    const newWindowTypes: WindowTypes[] =
      value === 'BOTH' ? ['DYNAMIC', 'REGULAR'] : [value as WindowTypes];

    setStoreState((oldState): StoreSettings => {
      assert(oldState);
      return { ...oldState, windowTypes: newWindowTypes };
    });
  };

  const onChangeFlags = (event: ChangeEvent<HTMLInputElement>): void => {
    const value = event.target.value;

    if (storeState.flags.includes(value)) {
      setStoreState((oldState): StoreSettings => {
        assert(oldState);
        return {
          ...oldState,
          flags: oldState.flags.filter(flag => flag !== value),
        };
      });
    } else {
      setStoreState((oldState): StoreSettings => {
        assert(oldState);
        return { ...oldState, flags: [...oldState.flags, value] };
      });
    }
  };

  const onChangeShopHomePage = (event: ChangeEvent<HTMLInputElement>): void => {
    const value = event.target.value;
    setOrClearStoreSettingsErrorFields('shopHomePageState', value);
    setStoreState((oldState): StoreSettings => {
      assert(oldState);
      return { ...oldState, shopHomePage: value };
    });
  };

  const onChangeDailyCutOffTime = (
    event: ChangeEvent<HTMLInputElement>
  ): void => {
    const value = event.target.value;
    setOrClearStoreSettingsErrorFields('dailyCutOffTimeState', value);
    setStoreState((oldState): StoreSettings => {
      assert(oldState);
      return { ...oldState, dailyCutOffTime: value };
    });
  };

  const onChangePackingCutOffTime = (
    event: ChangeEvent<HTMLInputElement>
  ): void => {
    const value = event.target.value;
    setOrClearStoreSettingsErrorFields('packagingCutoffState', value);
    setStoreState((oldState): StoreSettings => {
      assert(oldState);
      return { ...oldState, packagingCutoff: value };
    });
  };

  const onChangeLargeOffsetCutOffTime = (
    event: ChangeEvent<HTMLInputElement>
  ): void => {
    const value = event.target.value;
    setOrClearStoreSettingsErrorFields('largeOffsetCutOffState', value);
    setStoreState((oldState): StoreSettings => {
      assert(oldState);
      return { ...oldState, qbbCutoffAdjustmentMinutes: value };
    });
  };

  const onChangeDeliveryBasePrice = (
    event: ChangeEvent<HTMLInputElement>
  ): void => {
    const value = event.target.value;
    setOrClearStoreSettingsErrorFields('displayBasePriceState', value);
    setStoreState((oldState): StoreSettings => {
      assert(oldState);
      return { ...oldState, displayBasePriceCents: value };
    });
  };

  const onChangeTemperatureControlPriceAdjustment = (
    event: ChangeEvent<HTMLInputElement>
  ): void => {
    const value = event.target.value;
    setOrClearStoreSettingsErrorFields(
      'temperatureControlPriceAdjustment',
      value
    );
    setStoreState((oldState): StoreSettings => {
      assert(oldState);
      return { ...oldState, temperatureControlPriceAdjustment: value };
    });
  };

  const setOrClearDiscountThresholdsErrors = (
    discountThresholds: ValueBasedDiscount[]
  ) => {
    const isInvalid = (value: string) =>
      !/^\d+([.,]\d{1,2})?$/.test(value) || !/[^0]+/.test(value);

    const errors: ThresholdErrorField[] = discountThresholds.map(
      ({ threshold, discount }) => {
        return {
          threshold: isInvalid(threshold),
          discount: isInvalid(discount),
        };
      }
    );

    setInputErrorFields(currentErrorFields => ({
      ...currentErrorFields,
      discountThresholds: errors,
    }));
  };

  const onChangeThreshold = (
    discountThresholds: ValueBasedDiscount[]
  ): void => {
    setOrClearDiscountThresholdsErrors(discountThresholds);
    setStoreState((oldState): StoreSettings => {
      assert(oldState);
      return { ...oldState, discountThresholds: discountThresholds };
    });
  };

  const onCloseToast = () => {
    setToastOpen(false);
    if (toastConfig && toastConfig.type === 'success') {
      history.goBack();
    }
  };

  const buildStoreOptionsSection = (
    options: Record<string, string>
  ): JSX.Element[] => {
    return Object.entries(options).map(([flag, flagLabel]) => (
      <SwitchComponent
        key={flag}
        checked={storeState.flags.includes(flag)}
        name="test-switch"
        id="hub-customer"
        value={flag}
        onChange={onChangeFlags}
        label={{
          node: flagLabel,
          placement: 'top',
        }}
      />
    ));
  };

  return (
    <>
      <Helmet>
        <title>Users - Edit store settings</title>
      </Helmet>
      <UsersMenu />
      <section className="store-settings-list-edit">
        <div className="store-settings-list">
          <div className="store-edit-header">
            <h2>{storeState.name}</h2>
          </div>
          <StoreSettingsSection title="ORDERS">
            <div className="flex-box">
              <div className="orders-view">
                <div className="u-pal u-bg-white">
                  <p>Daily last order cut off time:</p>
                  <InputComponent
                    variant="outlined"
                    data-lpignore="true"
                    label=""
                    size="small"
                    id="daily-cut-off-time"
                    name="daily-cut-off-time"
                    value={storeState.dailyCutOffTime}
                    error={inputErrorFields.dailyCutOffTimeState}
                    onChange={onChangeDailyCutOffTime}
                  />
                  <div className="u-pal u-bg-white">
                    <p>Packing cut-off time:</p>
                    <InputComponent
                      variant="outlined"
                      data-lpignore="true"
                      label=""
                      size="small"
                      id="packaging-cut-off-time"
                      name="packaging-cut-off-time"
                      value={storeState.packagingCutoff}
                      error={inputErrorFields.packagingCutoffState}
                      onChange={onChangePackingCutOffTime}
                    />
                  </div>
                  <div className="u-pal u-bg-white">
                    <p>Order duplication detection:</p>
                    <RadioComponent
                      checked={storeState.duplicateDetection === 'NONE'}
                      name="None"
                      id="none"
                      onChange={onChangeDefaultOrderDuplication}
                      label={{ node: 'None' }}
                      value="NONE"
                    />
                    <RadioComponent
                      checked={
                        storeState.duplicateDetection === 'ORDER_REFERENCE'
                      }
                      name="Order reference"
                      id="order-reference"
                      onChange={onChangeDefaultOrderDuplication}
                      label={{ node: 'Order reference' }}
                      value="ORDER_REFERENCE"
                    />
                    <RadioComponent
                      checked={
                        storeState.duplicateDetection === 'CONSIGNOR_DUPLICATES'
                      }
                      name="Consignor duplicates"
                      id="consignor-duplicates"
                      onChange={onChangeDefaultOrderDuplication}
                      label={{ node: 'Consignor duplicates' }}
                      value="CONSIGNOR_DUPLICATES"
                    />
                  </div>
                  <div className="u-pal u-bg-white">
                    <p>Shop home page:</p>
                    <div className="homepage-url-field">
                      <InputComponent
                        id="standard"
                        label="Home page URL"
                        name="test-input"
                        size="medium"
                        value={storeState.shopHomePage || ''}
                        error={inputErrorFields.shopHomePageState}
                        onChange={onChangeShopHomePage}
                      />
                    </div>
                  </div>
                  <div className="u-pal u-bg-white">
                    <p>Consolidation support:</p>
                    <RadioComponent
                      checked={storeState.consolidationSupport === 'NONE'}
                      name="None"
                      id="none"
                      onChange={onChangeDefaultConsolidationSupport}
                      label={{ node: 'None' }}
                      value="NONE"
                    />
                    <RadioComponent
                      checked={storeState.consolidationSupport === 'ANONYMOUS'}
                      name="Anonymous"
                      id="anonymous"
                      onChange={onChangeDefaultConsolidationSupport}
                      label={{ node: 'Anonymous' }}
                      value="ANONYMOUS"
                    />
                    <RadioComponent
                      checked={
                        storeState.consolidationSupport === 'KNOWN_RECIPIENT'
                      }
                      name="Known recipient"
                      id="known-recipient"
                      onChange={onChangeDefaultConsolidationSupport}
                      label={{ node: 'Known recipient' }}
                      value="KNOWN_RECIPIENT"
                    />
                  </div>
                </div>
              </div>
              <div className="u-pal u-bg-white">
                <p>Daily large offset time (minutes):</p>
                <InputComponent
                  variant="outlined"
                  data-lpignore="true"
                  label=""
                  size="small"
                  id="daily-large-cut-off-time"
                  name="daily-large-cut-off-time"
                  value={storeState.qbbCutoffAdjustmentMinutes.toString()}
                  error={inputErrorFields.largeOffsetCutOffState}
                  onChange={onChangeLargeOffsetCutOffTime}
                />
              </div>
            </div>
          </StoreSettingsSection>
          <StoreSettingsSection title="PRICING">
            <div className="flex-box">
              <div className="prices">
                <div className="u-pal u-bg-white">
                  <p>Delivery base price:</p>
                  <InputComponent
                    variant="outlined"
                    data-lpignore="true"
                    label=""
                    size="small"
                    id="delivery-base-price"
                    name="delivery-base-price"
                    type="number"
                    value={storeState.displayBasePriceCents || ''}
                    error={inputErrorFields.displayBasePriceState}
                    onChange={onChangeDeliveryBasePrice}
                  />
                </div>
                <div className="u-pal u-bg-white">
                  <p>Temperature controlled price adjustment:</p>
                  <InputComponent
                    variant="outlined"
                    data-lpignore="true"
                    label=""
                    size="small"
                    id="temperature-price"
                    name="temperature-price"
                    type="number"
                    value={storeState.temperatureControlPriceAdjustment}
                    error={inputErrorFields.temperatureControlPriceAdjustment}
                    onChange={onChangeTemperatureControlPriceAdjustment}
                  />
                </div>
              </div>
              <div className="u-pal u-bg-white">
                <ThresholdInput
                  discountThresholds={storeState.discountThresholds}
                  onChange={onChangeThreshold}
                  errorFields={inputErrorFields.discountThresholds}
                />
              </div>
            </div>
          </StoreSettingsSection>
          <StoreSettingsSection title="AVAILABILITY">
            <div className="u-pal u-bg-white">
              <div className="regions-checkbox">
                <p>Regions:</p>
                {Object.keys(Region).map(storeRegion => {
                  return (
                    <CheckboxComponent
                      key={storeRegion}
                      checked={storeState.deliveryRegions.includes(storeRegion)}
                      name="test-checkbox"
                      value={storeRegion}
                      label={storeRegion}
                      handleChange={onChangeRegions}
                    />
                  );
                })}
              </div>
            </div>
            <div className="u-pal u-bg-white">
              <p>Delivery windows:</p>
              <RadioComponent
                checked={
                  storeState.windowTypes.length === 1 &&
                  storeState.windowTypes.includes('REGULAR')
                }
                name="Regular"
                id="regular"
                onChange={onChangeWindows}
                label={{ node: 'Regular - 2 hour windows' }}
                value="REGULAR"
              />
              <RadioComponent
                checked={
                  storeState.windowTypes.includes('DYNAMIC') &&
                  storeState.windowTypes.includes('REGULAR')
                }
                name="regular and dynamic"
                id="regular-and-dynamic"
                onChange={onChangeWindows}
                label={{ node: 'Regular and Dynamic windows' }}
                value="BOTH"
              />
              <RadioComponent
                checked={storeState.windowTypes.includes('SINGLE_LONG')}
                id="four-hour"
                name="four hour"
                onChange={onChangeWindows}
                label={{ node: 'Long windows (only 4/3 hours)' }}
                value="SINGLE_LONG"
              />
            </div>
          </StoreSettingsSection>
          <StoreSettingsSection title="STORE OPTIONS">
            <p>Capacity options</p>
            <div className="u-pal u-bg-white store-flags">
              {buildStoreOptionsSection(StoreCapacityFlagsWithLabel)}
            </div>
            <p>Retailer options</p>
            <div className="u-pal u-bg-white store-flags">
              {buildStoreOptionsSection(StoreRetailerFlagsWithLabel)}
            </div>
          </StoreSettingsSection>
          <div className="store-edit-buttons">
            <div className="u-pal u-bg-white">
              <ButtonComponent
                className="cancel-button"
                component={Link}
                to="/users/storesettings"
                text="CANCEL"
                variant="outlined"
              />

              <ButtonComponent
                className="apply-button"
                text="APPLY CHANGES"
                variant="contained"
                onClick={saveSettings}
                disabled={hasErrors(inputErrorFields)}
              />
            </div>
          </div>
        </div>
      </section>
      {toastConfig && (
        <Snackbar
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          open={isToastOpen}
          autoHideDuration={2000}
          onClose={onCloseToast}
        >
          <Alert onClose={onCloseToast} severity={toastConfig.type}>
            {toastConfig.text}
          </Alert>
        </Snackbar>
      )}
    </>
  );
};

export default EditStoreSettings;
